import React, { useState } from "react";
import { Link2, Unlink, CheckCircle2, Trash2, Loader } from "lucide-react";
import { ProjectSelector } from "./ProjectSelector";

interface BulkActionBarProps {
  selectedCount: number;
  onMapToProject: (projectId: string | null) => Promise<void>;
  onClearMapping: () => Promise<void>;
  onDeselectAll: () => void;
  onBulkDelete?: () => void;
  isLoading?: boolean;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onMapToProject,
  onClearMapping,
  onDeselectAll,
  onBulkDelete,
  isLoading = false,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  if (selectedCount === 0) {
    return null;
  }

  const handleMapToProject = () => {
    setShowMapModal(true);
  };

  const handleClearMapping = () => {
    setShowConfirmClear(true);
  };

  const confirmMap = async () => {
    await onMapToProject(selectedProjectId);
    setShowMapModal(false);
    setSelectedProjectId(null);
  };

  const confirmClear = async () => {
    await onClearMapping();
    setShowConfirmClear(false);
    setSelectedProjectId(null);
  };

  return (
    <>
      {/* Action Bar with Direct Actions */}
      <div className="mb-4 flex items-center justify-between gap-3 animate-fade-in flex-wrap">
        {/* Selection Badge */}
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg shadow-sm">
            <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
            </span>
          </div>
          <button
            onClick={onDeselectAll}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors font-medium hover:underline"
          >
            Clear
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleMapToProject}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all font-medium text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Link2 className="w-4 h-4" />
            <span>Map to project</span>
          </button>

          <button
            onClick={handleClearMapping}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:shadow-md hover:border-gray-400 dark:hover:border-gray-500 transition-all font-medium text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Unlink className="w-4 h-4" />
            <span>Clear mapping</span>
          </button>

          {onBulkDelete && (
            <button
              onClick={() => onBulkDelete()}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete selected</span>
            </button>
          )}
        </div>
      </div>

      {/* Map to Project Modal */}
      {showMapModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Map to Project
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select a project to map <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedCount}</span> {selectedCount === 1 ? "bill" : "bills"} to. This will link them for profit & loss reporting.
            </p>
            <div className="mb-6">
              <ProjectSelector
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                placeholder="Select project..."
                disabled={isLoading}
                dropdownZIndex={10002}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowMapModal(false);
                  setSelectedProjectId(null);
                }}
                className="px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmMap}
                disabled={isLoading || selectedProjectId === null}
                className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Mapping...
                  </>
                ) : (
                  "Map to Project"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Clearing */}
      {showConfirmClear && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Unlink className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Clear Mapping
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Remove project mapping from <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedCount}</span> {selectedCount === 1 ? "bill" : "bills"}? They will no longer appear in project profit & loss reports.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmClear(false)}
                className="px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmClear}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  "Clear Mapping"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
